import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import config from '../config'
import { getQueryString } from '@/helper'
import { fetchCat } from '@/helper/permission.helper'
import { isMobileTablet, deviceId } from '../helper/device_info'
import { initialize, saveUserId, saveContactId } from '@/services'

Vue.use(VueRouter)

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'login',
    component: () => import('../views/Login.vue'),
    meta: {
      requiresGuest: true
    }
  },
  {
    path: '/recovery',
    name: 'recovery',
    component: () => import('../views/PasswordRecovery.vue'),
    meta: {
      requiresGuest: true
    }
  },
  {
    path: '/',
    component: () => import('../views/SecureRoutes.vue'),
    children: [
      {
        path: 'library',
        name: 'library',
        component: () => import('../views/Library.vue'),
        meta: {
          requiresAuth: true,
          clientAuth: true
        }
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('../views/settings/index.vue'),
        meta: {
          requiresAuth: true
        },
        children: [
          {
            path: 'profile',
            name: 'profile-settings',
            component: () => import('../views/settings/Profile.vue')
          },
          {
            path: 'password',
            name: 'password-settings',
            component: () => import('../views/settings/Password.vue')
          },
          {
            path: 'purchase-history',
            name: 'purchase-history',
            component: () => import('../views/settings/PurchaseHistory.vue')
          }
        ]
      },
      {
        path: 'products',
        component: () => import('../views/ProductContainer.vue'),
        meta: {
          requiresAuth: true,
          adminUser: true
        },
        children: [
          {
            path: ':id',
            name: 'product-overview',
            component: () => import('../views/Products.vue')
          },
          {
            path: ':id/categories',
            name: 'category-list',
            component: () => import('../views/CategoryList.vue')
          },
          {
            path: ':id/categories/:category_id',
            name: 'category-overview',
            component: () => import('../views/CategoryDetail.vue')
          },
          {
            path: ':id/categories/:category_id/posts/:post_id',
            name: 'post-overview',
            component: () => import('../views/Post.vue')
          }
        ]
      }
    ]
  },

  {
    path: '/offers/:id',
    name: 'offer',
    component: () => import('../views/Checkout.vue')
  },
  { path: '/404', component: () => import('../views/NotFound.vue') },
  { path: '*', redirect: '/404' }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

router.beforeEach(async (to, from, next) => {
  let cat
  try {
    cat = fetchCat()
  } catch (error) {
    cat = undefined
  }

  if (to.matched.some(record => record.meta.requiresGuest)) {
    if (cat && cat.userId) {
      next({
        path: '/library'
      })
    } else if (to.query.loginCode && (to.query.user || to.query.email)) {
      try {
        let user, type
        if (to.query.user) {
          user = to.query.user
          type = 'id'
        } else {
          user = getQueryString(window.location.search, 'email')
          type = 'email'
        }
        const { data } = await axios.post(
          `${config.baseUrl}/user/login-code/${to.query.loginCode}`,
          {
            user: user,
            type: type,
            deviceName: navigator.userAgent,
            deviceId: deviceId,
            deviceType: isMobileTablet() ? 'mobile' : 'desktop',
            hostName: to.query.domain || window.location.hostname
          }
        )
        if (data.token) {
          const payload = btoa(JSON.stringify(data))
          if (localStorage) {
            localStorage.setItem('cat', payload)
          }

          initialize(data.locationId)
          saveUserId(data.userId)
          saveContactId(data.contactId)

          next({
            path: '/library'
          })
        }
      } catch (e) {
        console.error('Error ---->', e)
        next({
          path: '/'
        })
      }
    } else {
      next()
    }
  } else if (to.matched.some(record => record.meta.adminUser)) {
    const { location_id: locationId, token } = to.query
    if (token) {
      const { data } = await axios.post(
        config.baseUrl + '/user/login-token',
        {
          locationId
        },
        {
          headers: {
            Authorization: 'Bearer ' + token
          }
        }
      )
      await firebase.auth().signInWithCustomToken(data.token)

      const payload = btoa(JSON.stringify(data))
      if (localStorage) {
        localStorage.setItem('cat', payload)
      }
      window.$cookies.set('cat', payload)
    }

    next()
  } else if (
    to.matched.some(record => record.meta.clientAuth) &&
    to.query.token
  ) {
    const { location_id: locationId, token } = to.query
    if (token) {
      await axios
        .post(config.baseUrl + '/user/login-ctoken', {
          locationId,
          ct: token
        })
        .then(async ({ data }) => {
          await firebase.auth().signInWithCustomToken(data.token)

          const payload = btoa(JSON.stringify(data))
          if (localStorage) {
            localStorage.setItem('cat', payload)
          }
          window.$cookies.set('cat', payload)
        })
        .catch(() => {
          next({
            name: 'login'
          })
        })
    }

    next()
  } else if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!cat) {
      next({
        name: 'login',
        query: {
          redirect: to.fullPath
        }
      })
    }
  }
  next()
})

export default router
